/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';

import {
  DialogButton,
  Dropdown,
} from '@beeinventor/dasiot-react-component-lib';
import {
  Box,
  Dialog as MuiDialog,
  styled,
  SxProps,
  Typography,
} from '@mui/material';

import CrossSvgIcon from '../../../components/SvgIcon/CrossSvgIcon';
import InputField from '../../../components/TextField/InputField';
import SettingInput from './SettingInput';

import {
  createTask,
  getDasloopFirmwareVersion,
  getDasloopProtocolSchemas,
  getDassensesFirmwareVersion,
  UpdateConfigEntriesType,
  UpdateConfigPayload,
  UpdatedConfigParams,
  updateTask,
} from '../../../apis/tasksApi';
import DropdownSearch from '../../../components/DropdownSearch';
import { InputValidationError } from '../../../types';
import {
  getSchemaVersion,
  parseFloat as parseFirmwareVersion,
} from '../../../types/schema';
import { TaskTypeConfig } from '../../../types/Task';
import AddSettingButton from './AddSettingButton';
import CheckErrorButton from './CheckErrorButton';

import { Add } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import WarningDialog from '../../../components/WarningDialog';
import { DasEndpoint, DasEndpointType } from '../../../types/Device';
import SelectDeviceList from '../add-device/SelectDeviceList';
import DasIdListPanel from './DasIdListPanel';
import ErrorMessage from './ErrorMessage';
import { convertInputToJsonFormat } from './settingType';

const dropdownSx: SxProps = {
  padding: 0,
  paddingLeft: '16px',
  maxWidth: '456px',
};

const Title = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.secondary.$60,
}));

const SpanRequire = styled('span')`
  color: ${({ theme }) => theme.color.highlight};
`;

interface ContainerDropdownProps {
  isSelected?: boolean;
}

const SettingInputContainer = styled('div')`
  display: flex;
  flex-direction: column;

  padding-top: 10px;
`;

// prettier-ignore
const ContainerDropdown = styled('div', {shouldForwardProp: (prop)=> prop !== "isSelected"})<ContainerDropdownProps>`
    margin-bottom: 10px;

  & > .Dropdown-root {
    
    border-radius: 4px;
    color: ${({ theme }) => theme.color.secondary.$100};
    font-weight: 400;
    font-size: 14px;
    border: 1px solid rgba(243, 243, 243, 1);
    height: 40px;
    min-width: 160px;
    background: #f5f5f5;
    & > .Dropdown-icon {
      width: 32px;
      height: 32px;
    }
    transition-duration: 0.3s;

    &:hover {
      background: #f3f3f3;
      border: 1px solid #ffce4f;
    }
  }

  & > .Dropdown--disabled {
    background: #e5e5e5;
    color: ${({ theme }) => theme.color.secondary.$80};
  }
  & div[role='tooltip'] {
    & > .MuiBox-root {
      color: ${({ theme }) => theme.color.secondary.$100};
      font-weight: 400;
      font-size: 14px;
      background: #e5e5e5;
      max-height: 200px;
      overflow-y: auto;


      & > .Dropdown-item {
        height: 40px;
        font-size: 12px;
        font-weight: 400;
      }
    }
    z-index: 1;
  }
`;

const Dialog = styled(MuiDialog)(({ theme }) => {
  return {
    '& .MuiPaper-root': {
      minWidth: '800px',
      '& > .header': {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        height: '64px',
        padding: '16px',
        '& > .title': {
          ...theme.typography.body1,
          flex: 1,
          fontWeight: '500',
          textAlign: 'center',
        },
        '& > .close-icon': {
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          '&:before': {
            display: 'block',
            content: '""',
            width: '2px',
            height: '30px',
            backgroundColor: theme.color.secondary.$40,
            marginRight: '20px',
          },
        },
      },
      '& > .banner': {
        color: '#fff',
        fontSize: '1rem',
        padding: '16px',
        '&[data-status="error"]': {
          background: theme.color.highlight,
        },
        '&[data-status="success"]': {
          background: theme.color.green.$100,
        },
      },
      '& > .content': {
        maxHeight: '640px',
        padding: '24px',
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        overflowY: 'auto',
        gap: '15px',
        '& > .right': {
          '& > .schema': {
            display: 'grid',
            gap: '5px',
            gridTemplateColumns: '1fr 150px',
          },
          '& > .container-input': {
            maxHeight: '100px',
            overflowY: 'auto',
          },
        },
      },
      '& > .container-content': {
        display: 'grid',
        padding: '0px 24px',
        gridTemplateColumns: '1fr 1fr 1fr',
        'column-gap': '16px',
      },
      '& > .action': {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '24px',
      },
    },
  };
});

const TASK_TYPES = [
  {
    id: 'GetConfig',
    name: 'Get Config',
    value: 'GetConfig',
  },
  {
    id: 'UpdateConfig',
    name: 'Update Config',
    value: 'UpdateConfig',
  },
  {
    id: 'UpdateFirmware',
    name: 'Update Firmware',
    value: 'UpdateFirmware',
  },
  {
    id: 'RebootDevice',
    name: 'Reboot Device',
    value: 'RebootDevice',
  },
];

const ErrorText = styled(Typography)`
  margin-top: 5px;
  padding: 3px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #ff4500;
  background: rgba(255, 69, 0, 0.1);
  text-align: center;
`;

interface CreateTaskDialogProps {
  handleClose: () => void;
  handleTaskListRefetch: () => void;
  LIST_PROJECT: { name: string; id: string; value: string }[];
}

interface DasIdListProps {
  value: string;
  isChecked: undefined | boolean;
  endpointId: string;
}

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  handleClose,
  handleTaskListRefetch,
  LIST_PROJECT,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<
    string | undefined
  >();
  const [selectedTaskType, setSelectedTaskType] =
    useState<(typeof TASK_TYPES)[number]['id']>();
  const [selectedFirmwareVersion, setSelectedFirmwareVersion] = useState<
    string | undefined
  >();
  const [selectedSchemaVersion, setSelectedSchemaVersion] = useState<
    string | undefined
  >();
  const [taskName, setTaskName] = useState<string | undefined>('');
  // const isChecked = false;
  const [inputDasIDs, setInputDasIds] = useState<DasIdListProps[]>([]);
  const [inputItems, setInputItems] = useState<string[]>([] as string[]);
  const [inputsValue, setInputsValue] = useState<{
    [key: string]: string | string[];
  }>({});

  const [isPassed, setIsPassed] = useState<boolean | undefined>(undefined);
  const [isChecked, setIsChecked] = useState<boolean | undefined>(undefined);
  const [getConfigApiResponse, setGetConfigApiResponse] = useState<any>();
  const [projectFormatError, setProjectFormatError] =
    useState<InputValidationError>({
      isError: false,
      message: '',
    });
  const [schemaFormatError, setSchemaFormatError] =
    useState<InputValidationError>({
      isError: false,
      message: '',
    });
  const [taskTypeError, setTaskTypeError] = useState<InputValidationError>({
    isError: false,
    message: '',
  });
  const [taskNameFormatError, setTaskNameFormatError] =
    useState<InputValidationError>({
      isError: false,
      message: '',
    });

  const [isAddDeviceMode, setIsDeviceMode] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedDevices, setSelectedDevices] = useState<DasEndpoint[]>([]);
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<DasEndpointType>('dasloop');
  const [hasAcknowledgedWarning, setHasAcknowledgedWarning] = useState(false);
  const checkProjectFormat = () => {
    if (!selectedProjectId) {
      setProjectFormatError({
        isError: true,
        message: 'No project selected',
      });
      return;
    }
    setProjectFormatError({ isError: false, message: '' });
  };

  const checkSchemaFormat = () => {
    if (!selectedSchemaVersion) {
      return setSchemaFormatError({
        isError: true,
        message: 'No schema version selected',
      });
    }

    return setSchemaFormatError({
      isError: false,
      message: '',
    });
  };

  const checkTaskNameFormat = () => {
    if (taskName === '') {
      return setTaskNameFormatError({
        isError: true,
        message: 'Required',
      });
    }

    return setTaskNameFormatError({
      isError: false,
      message: '',
    });
  };

  const checkTaskTypeFormat = () => {
    if (!selectedTaskType) {
      return setTaskTypeError({
        isError: true,
        message: 'No task type selected',
      });
    }

    return setTaskTypeError({
      isError: false,
      message: '',
    });
  };

  const inputEntry = useMemo(() => {
    if (inputsValue) {
      return Object.entries(inputsValue).reduce<any[]>(
        (accumulator, [key, value]) => {
          if (value !== '') {
            accumulator.push(
              convertInputToJsonFormat({
                unparseString: key,
                inputValue: value,
              }),
            );
          }
          return accumulator;
        },
        [],
      );
    }
    return [];
  }, [inputsValue]);

  const updateConfigJsonDataFormat = useMemo(() => {
    if (selectedTaskType === 'UpdateConfig') {
      const updateConfigParams = {
        name: '',
        projectId: '',
        endpointIds: [] as string[],
        task: {
          type: '',
          payload: {
            schemaVersion: '',
            entries: [] as UpdateConfigEntriesType[],
          },
        },
      };
      const endpointList = inputDasIDs.reduce((previous, current) => {
        if (current.isChecked && current.endpointId) {
          previous.push(current.endpointId);
        }
        return previous;
      }, [] as string[]);

      updateConfigParams.name = taskName ? taskName : '';
      updateConfigParams.endpointIds = endpointList;
      updateConfigParams.task.type = selectedTaskType;
      updateConfigParams.task.payload.schemaVersion =
        selectedSchemaVersion ?? '';
      updateConfigParams.task.payload.entries = inputEntry ?? [];
      updateConfigParams.projectId = selectedProjectId ?? '';
      return updateConfigParams;
    }
  }, [inputDasIDs, taskName, selectedTaskType, selectedProjectId, inputsValue]);

  const { data: dasloopProtocolSchema } = useQuery({
    queryKey: ['get-protocol-schema'],
    queryFn: async () => {
      const result = await getDasloopProtocolSchemas();
      return result.data.data;
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: deviceFirmwareVersion } = useQuery({
    queryKey: ['get-firmware-version', selectedDeviceType],
    queryFn: async () => {
      const result =
        selectedDeviceType === 'dassenses'
          ? await getDassensesFirmwareVersion()
          : await getDasloopFirmwareVersion();
      return result.data.data;
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  const getConfigJsonDataFormat = useMemo(() => {
    if (selectedTaskType === 'GetConfig') {
      const getConfigParams = {
        name: '',
        projectId: '',
        endpointIds: [] as string[],
        task: {
          type: 'GetConfig',
        },
      };

      const endpointList = inputDasIDs.reduce((previous, current) => {
        if (current.isChecked && current.endpointId) {
          previous.push(current.endpointId);
        }
        return previous;
      }, [] as string[]);

      getConfigParams.endpointIds = endpointList;
      getConfigParams.name = taskName ?? '';
      getConfigParams.projectId = selectedProjectId ?? '';
      return getConfigParams;
    }
  }, [inputDasIDs, taskName, selectedTaskType, selectedProjectId, inputsValue]);

  const updateFirmwareJsonDataFormat = useMemo(() => {
    if (selectedTaskType === 'UpdateFirmware') {
      const updateFirmwareParams = {
        name: '',
        projectId: '',
        endpointIds: [] as string[],
        task: {
          type: 'UpdateFirmware',
          payload: {
            version: '',
          },
        },
      };

      const endpointList = inputDasIDs.reduce((previous, current) => {
        if (current.isChecked && current.endpointId) {
          previous.push(current.endpointId);
        }
        return previous;
      }, [] as string[]);

      updateFirmwareParams.endpointIds = endpointList;
      updateFirmwareParams.name = taskName ?? '';
      updateFirmwareParams.projectId = selectedProjectId ?? '';
      updateFirmwareParams.task.payload.version = selectedFirmwareVersion ?? '';
      return updateFirmwareParams;
    }
  }, [
    inputDasIDs,
    taskName,
    selectedTaskType,
    selectedProjectId,
    selectedFirmwareVersion,
  ]);

  const rebootDeviceJsonDataFormatArr = useMemo(() => {
    if (selectedTaskType !== 'RebootDevice') return [];

    const endpointList = inputDasIDs.reduce((previous, current) => {
      if (current.isChecked && current.endpointId) {
        previous.push(current.endpointId);
      }
      return previous;
    }, [] as string[]);

    if (!endpointList.length) return [];

    return [
      {
        name: taskName ?? '',
        projectId: selectedProjectId ?? '',
        endpointIds: endpointList,
        taskType: 'RebootDevice' as TaskTypeConfig,
      },
    ];
  }, [selectedTaskType, inputDasIDs, taskName, selectedProjectId]);

  const handleApiCalling = async () => {
    switch (selectedTaskType) {
      case 'GetConfig': {
        const temp = await createTask({
          name: getConfigJsonDataFormat?.name as string,
          projectId: getConfigJsonDataFormat?.projectId as string,
          endpointIds: getConfigJsonDataFormat?.endpointIds as string[],
          taskType: getConfigJsonDataFormat?.task.type as TaskTypeConfig,
        });
        setGetConfigApiResponse(temp);
        handleTaskListRefetch();
        handleClose();
        break;
      }
      case 'UpdateConfig': {
        //useMutation
        if (updateConfigJsonDataFormat) {
          const updatedConfigParams: UpdatedConfigParams = {
            name: taskName as string,
            projectId: selectedProjectId as string,
            endpointIds: updateConfigJsonDataFormat?.endpointIds as string[],
            taskType: updateConfigJsonDataFormat?.task.type as TaskTypeConfig,
            payload: updateConfigJsonDataFormat?.task
              .payload as UpdateConfigPayload,
          };
          await updateTask(updatedConfigParams);
        }
        handleTaskListRefetch();
        handleClose();
        break;
      }
      case 'UpdateFirmware': {
        if (updateFirmwareJsonDataFormat) {
          const updatedFirmwareParams = {
            name: taskName as string,
            projectId: selectedProjectId as string,
            endpointIds: updateFirmwareJsonDataFormat?.endpointIds as string[],
            taskType: updateFirmwareJsonDataFormat?.task.type as TaskTypeConfig,
            payload: updateFirmwareJsonDataFormat?.task
              .payload as UpdateConfigPayload,
          };
          await updateTask(updatedFirmwareParams);
        }
        handleTaskListRefetch();
        handleClose();
        break;
      }
      case 'RebootDevice': {
        if (rebootDeviceJsonDataFormatArr.length) {
          const results = await Promise.allSettled(
            rebootDeviceJsonDataFormatArr.map((item) => {
              return createTask({
                name: item.name || (taskName as string),
                projectId: item.projectId || (selectedProjectId as string),
                endpointIds: item.endpointIds,
                taskType: item.taskType,
              });
            }),
          );

          const rejected = results.filter((r) => r.status === 'rejected');
          if (rejected.length) {
            console.warn(
              `RebootDevice: ${rejected.length} of ${results.length} tasks failed`,
              rejected,
            );
          }
        }
        handleTaskListRefetch();
        handleClose();
        break;
      }
      default:
        throw new Error(
          `unsupported API calling type: ${selectedTaskType ?? 'undefined'}`,
        );
    }
    //activate API
  };

  const settingList = useMemo(() => {
    if (selectedSchemaVersion && dasloopProtocolSchema) {
      return (dasloopProtocolSchema[selectedSchemaVersion] ?? []).map((d) => ({
        name: Object.keys(d)[0],
        value: `${Object.keys(d)[0]} - ${Object.values(d)[0]}`,
      }));
    }
    return [];
  }, [selectedSchemaVersion, dasloopProtocolSchema]);

  const schemaVersion: Array<{
    id: string;
    name: string;
    value: string;
  }> = useMemo(() => {
    if (dasloopProtocolSchema) {
      return Object.keys(dasloopProtocolSchema).map((version) => ({
        id: version,
        name: version,
        value: version,
      }));
    }
    return [];
  }, [dasloopProtocolSchema]);

  const firmWareVersion: Array<{
    id: string;
    name: string;
    value: string;
  }> = useMemo(() => {
    if (deviceFirmwareVersion) {
      return deviceFirmwareVersion.map((device) => ({
        id: device.firmwareVersion,
        name: device.firmwareVersion,
        value: device.firmwareVersion,
      }));
    }
    return [];
  }, [deviceFirmwareVersion]);

  const handleCloseInputItem = (value: string) => {
    // Filter the item we want to remove
    setInputItems((prev) => prev.filter((d) => d !== value));
    // Remove the corresponding value from inputsValue
    setInputsValue((prev) => {
      const { [value]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleAddSettingClick = (value: string) => {
    // Use callback form to get latest state
    setInputItems((prevItems) => {
      if (!prevItems.includes(value)) {
        return [...prevItems, value];
      }
      return prevItems;
    });
  };

  const settingInputItems = inputItems.map((d, index) => {
    return (
      <SettingInput
        key={`${d}-${index}`}
        value={settingList.find((setting) => setting.value === d)?.value ?? ''}
        handleOnChange={(e) => {
          setInputsValue((prev) => ({ ...prev, [d]: e }));
        }}
        handleCloseInputItem={(value: string) => handleCloseInputItem(value)}
      />
    );
  });

  useEffect(() => {
    if (selectedProjectId) {
      setProjectFormatError({ isError: false, message: '' });
    }
  }, [selectedProjectId]);

  const isDasloopVersionisHigher = useMemo(() => {
    if (!selectedSchemaVersion || !selectedDevices.length) return false;

    let selectedSchemaNumeric: number;
    try {
      selectedSchemaNumeric = parseFirmwareVersion(selectedSchemaVersion);
    } catch {
      return false;
    }

    return selectedDevices.some((device) => {
      const fw = device.shadow?.dataPoint?.firmwareVersion?.value;
      if (!fw) return false;
      try {
        return parseFirmwareVersion(fw) > selectedSchemaNumeric;
      } catch {
        return false;
      }
    });
  }, [selectedDevices, selectedSchemaVersion]);

  useEffect(() => {
    setHasAcknowledgedWarning(false);
  }, [selectedSchemaVersion, selectedDevices]);

  const isWarningOpen =
    selectedTaskType === 'UpdateConfig' &&
    isDasloopVersionisHigher &&
    !hasAcknowledgedWarning &&
    !isAddDeviceMode;

  // Temporarily hidden for DasSenseH tasks.
  const availableTaskTypes =
    selectedDeviceType === 'dassenses'
      ? TASK_TYPES.filter(
          (taskType) =>
            taskType.id !== 'GetConfig' && taskType.id !== 'UpdateConfig',
        )
      : TASK_TYPES;

  return (
    <Dialog open onClose={handleClose}>
      <div className="header">
        <span className="title">
          {isAddDeviceMode ? 'Add Devices' : 'Add New Task'}
        </span>
        <div className="close-icon" onClick={handleClose}>
          <CrossSvgIcon sx={{ width: '40px', height: '40px' }} />
        </div>
      </div>
      {isAddDeviceMode ? (
        <SelectDeviceList
          onBack={() => setIsDeviceMode(false)}
          onDevicesSelect={(devices) => {
            setInputDasIds(devices);
            setSelectedDeviceIds(
              new Set(
                devices.filter((d) => d.isChecked).map((d) => d.endpointId),
              ),
            );
            const selectedDevices = devices
              .map((d) => d.device)
              .filter((device): device is DasEndpoint => device !== undefined);
            setSelectedDevices(selectedDevices);
          }}
          onDeviceTypeChange={(type) => {
            setSelectedDeviceType(type);
            setSelectedFirmwareVersion(undefined);
            if (type === 'dassenses' && selectedTaskType === 'GetConfig') {
              setSelectedTaskType(undefined);
              setTaskName('');
            }
          }}
          selectedProjectId={selectedProjectId}
          initialSelectedDevices={selectedDeviceIds}
          initialSelectedDeviceObjects={selectedDevices}
          initialDeviceType={selectedDeviceType}
        />
      ) : (
        <div
          className="container-content"
          style={isChecked ? { opacity: '0.4', pointerEvents: 'none' } : {}}
        >
          <ContainerDropdown>
            <Title>
              Project
              <SpanRequire>*</SpanRequire>
            </Title>
            <DropdownSearch
              data={LIST_PROJECT}
              onSelect={function (d): void {
                setSelectedProjectId(d.value as string);
              }}
              onBlur={checkProjectFormat}
              selectedId={selectedProjectId}
              placeholder="Choose one"
              error={projectFormatError}
            />
          </ContainerDropdown>

          <ContainerDropdown>
            <Title>
              Type<SpanRequire>*</SpanRequire>
            </Title>
            <Dropdown
              mode="light"
              sx={dropdownSx}
              list={availableTaskTypes}
              onSelect={(v) => {
                setSelectedTaskType(v as string);
                setTaskName(v as string);
              }}
              selectedId={selectedTaskType}
              placeholder="Task Type"
              onBlur={checkTaskTypeFormat}
              popperProps={{
                disablePortal: true,
                placement: 'bottom-start',
              }}
            />
            {taskTypeError.isError && (
              <ErrorMessage message={taskTypeError.message} />
            )}
          </ContainerDropdown>
          <div>
            <InputField
              title={'Task Name'}
              name={'Task Name'}
              value={taskName ?? selectedTaskType}
              isRequired
              onChange={(e) => setTaskName(e.target.value as string)}
              onBlur={checkTaskNameFormat}
              containerProps={{ style: { margin: '0px 0' } }}
            />
            {taskNameFormatError.isError && (
              <ErrorMessage message={taskNameFormatError.message} />
            )}
          </div>

          <div className="">
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Title>
                Device<SpanRequire>*</SpanRequire>
              </Title>
              <Box sx={{ display: 'flex' }}>
                <Title sx={inputDasIDs.length >= 10 ? { color: 'red' } : {}}>
                  {inputDasIDs.length}
                </Title>
                <Title>/10</Title>
              </Box>
            </Box>
            <DialogButton
              variant="contained"
              color="primary"
              onClick={() => setIsDeviceMode(true)}
              sx={{ height: '32px', borderRadius: '16px', minWidth: '71px' }}
              endIcon={<Add />}
              disabled={!selectedProjectId || inputDasIDs.length > 10}
            >
              Add
            </DialogButton>
            <DasIdListPanel dasIdList={inputDasIDs} />
          </div>
          <Box sx={{ gridColumn: '2 / span 2' }}>
            {selectedTaskType === 'UpdateConfig' && (
              <Box>
                <Box
                  className="schema"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 150px',
                    gap: '5px',
                  }}
                >
                  <ContainerDropdown sx={{ margin: 'auto 0' }}>
                    <Title>
                      Schema Version<SpanRequire>*</SpanRequire>
                    </Title>
                    <Dropdown
                      mode="light"
                      sx={{ ...dropdownSx, width: '100%' }}
                      list={schemaVersion}
                      onSelect={(v) => {
                        setSelectedSchemaVersion(v as string);
                      }}
                      selectedId={selectedSchemaVersion}
                      placeholder="Select Schema Version"
                      onBlur={checkSchemaFormat}
                      popperProps={{
                        disablePortal: true,
                        placement: 'bottom-start',
                      }}
                    />
                  </ContainerDropdown>
                  <AddSettingButton
                    selectedSchema={selectedSchemaVersion}
                    setInputItems={(value: string) =>
                      handleAddSettingClick(value)
                    }
                    settingList={settingList}
                    errorMessage={undefined}
                  />
                  {inputItems.length === 0 && (
                    <ErrorMessage message={'No configuration(s)'} />
                  )}
                </Box>

                <SettingInputContainer>
                  {settingInputItems}
                </SettingInputContainer>
              </Box>
            )}

            {selectedTaskType === 'UpdateFirmware' && (
              <>
                <ContainerDropdown>
                  <Title>
                    Firmware Version<SpanRequire>*</SpanRequire>
                  </Title>
                  <Dropdown
                    mode="light"
                    sx={{ ...dropdownSx, maxWidth: '100%' }}
                    list={firmWareVersion}
                    onSelect={(v) => {
                      setSelectedFirmwareVersion(v as string);
                    }}
                    selectedId={selectedFirmwareVersion}
                    placeholder="Select Firmware Version"
                    popperProps={{
                      disablePortal: true,
                      placement: 'bottom-start',
                    }}
                  />
                </ContainerDropdown>
              </>
            )}
          </Box>
        </div>
      )}

      {!isAddDeviceMode && (
        <div className="action">
          {isChecked ? (
            <>
              <DialogButton
                sx={(theme) => ({ color: theme.color.primary.$100 })}
                variant="text"
                color="primary"
                onClick={() => setIsChecked(false)}
              >
                Back
              </DialogButton>
              <DialogButton
                variant="contained"
                color="primary"
                onClick={handleApiCalling}
              >
                CREATE
              </DialogButton>
            </>
          ) : (
            <CheckErrorButton
              isPassed={isPassed}
              isChecked={isChecked}
              setIsPassed={(state) => setIsPassed(state)}
              setIsChecked={(state) => setIsChecked(state)}
            />
          )}
        </div>
      )}
      <WarningDialog
        open={isWarningOpen}
        title="Warning"
        description={
          <>
            You are trying to update config using an older schema than the
            current DasLoop firmware version.
            <br />
            Make sure the firmware has no add/remove configurations.
          </>
        }
        onClose={() => setHasAcknowledgedWarning(true)}
        onSubmit={() => setHasAcknowledgedWarning(true)}
      />
    </Dialog>
  );
};

export default CreateTaskDialog;
